import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Form,
  InputGroup,
  Stack,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';

import { faClose, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import useLocalStore from 'src/store/useLocalStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';

import { DEFAULT_COLS_KEY } from 'src/models/AIIntegratedResultsTab.model';
import { getColumnKey } from 'src/models/ColumnConfiguration.model';
import { SortOrder, SortOrderEnum } from 'src/models/GalenData/SortOrder.model';
import { Session } from 'src/models/Session.model';

import useGetExamGetPendingStatusText from 'src/hooks/useGetExamPendingStatusText';
import useUiTranslation from 'src/hooks/useUiTranslation';

import getSessionsImageFiles from 'src/utils/getSessionsImageFiles';
import { validateSearchValue } from 'src/utils/validationRules';

export const defaultPageSize = 10;

interface NewResultsPageProps {
  extraColumns?: Column<Session>[];
  currentPage: number;
  currentSearchValue: string;
  handleSetCurrentPage: (page: number) => void;
  handleSetCurrentSearchValue: (searchValue: string) => void;
  isLoading: boolean;
  dataResource: {
    sessions: Session[];
    totalElements: number;
    totalPages: number;
  };
  handleLoadSessions: (
    page: number,
    pageSize: number,
    searchValue: string,
    sessionsSortOrder: SortOrder,
  ) => Promise<void>;
}

interface FormValues {
  searchValue: string;
}

const OHResults = ({
  extraColumns,
  currentPage,
  currentSearchValue,
  handleSetCurrentPage,
  handleSetCurrentSearchValue,
  isLoading,
  dataResource,
  handleLoadSessions,
}: NewResultsPageProps) => {
  const { setAIIntegratedResultsColsKeys, AIIntegratedResultsColsKeys } =
    useLocalStore();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setFocus,
    getValues,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      searchValue: '',
    },
  });

  const handleReset = () => {
    reset();
    handleSetCurrentPage(1);
    handleSetCurrentSearchValue('');
    handleLoadSessions(0, defaultPageSize, '', SortOrderEnum.DESC);
  };
  const { tCommon, tPages } = useUiTranslation();
  const handleSearch = useCallback(
    (data: { searchValue: string }) => {
      const { searchValue } = data;
      handleSetCurrentSearchValue(searchValue);
      handleLoadSessions(0, defaultPageSize, searchValue, SortOrderEnum.DESC);
      handleSetCurrentPage(1);
    },
    [handleLoadSessions, handleSetCurrentPage, handleSetCurrentSearchValue],
  );

  const submitRef = useRef<HTMLFormElement>(null);

  const location = useLocation<{
    keyword: string;
  }>();

  useEffect(() => {
    if (location.state?.keyword) {
      setValue('searchValue', location.state?.keyword, {
        shouldValidate: true,
      });
      setFocus('searchValue');
    }
    submitRef.current?.requestSubmit();
  }, [
    handleLoadSessions,
    handleSearch,
    handleSubmit,
    location.state?.keyword,
    setFocus,
    setValue,
  ]);

  const getPendingStatusText = useGetExamGetPendingStatusText();

  const columns: Column<Session>[] = useMemo(
    () => [
      {
        title: tCommon('patientFields.userEmail'),
        key: DEFAULT_COLS_KEY.USERNAME,
      },
      {
        title: tCommon('patientFields.dateTime'),
        dataIndex: DEFAULT_COLS_KEY.DATE_TIME,
        render: (record: Session) => (
          <div>
            <b>{record.date}</b>
            <p>{record.time}</p>
          </div>
        ),
      },

      {
        title: tCommon('patientFields.patientName'),
        dataIndex: DEFAULT_COLS_KEY.PATIENT_NAME,
        render: (record) => (
          <div>
            {record.firstName} {record.lastName}
          </div>
        ),
      },
      {
        title: tCommon('patientFields.patientID'),
        key: DEFAULT_COLS_KEY.ID,
      },
      {
        title: tCommon('patientFields.DOB'),
        key: DEFAULT_COLS_KEY.DOB,
      },
      {
        title: (
          <div>
            <span>{tPages('results.sessionDetails')}</span>
            <p>({tPages('results.numberOfAttempts')})</p>
          </div>
        ),
        dataIndex: DEFAULT_COLS_KEY.ATTEMPTS,
        render: (record) => {
          const { leftImageFileContainer, rightImageFileContainer } =
            getSessionsImageFiles([record, ...(record.retakeSessions ?? [])]);

          return (
            <div>
              {record.graded ? (
                <Stack gap={2}>
                  <div>
                    {tPages('newExam.leftEye')} (
                    {leftImageFileContainer.length > 0
                      ? leftImageFileContainer.length
                      : tCommon('N/A')}
                    )
                  </div>
                  <div>
                    {tPages('newExam.rightEye')} (
                    {rightImageFileContainer.length > 0
                      ? rightImageFileContainer.length
                      : tCommon('N/A')}
                    )
                  </div>
                </Stack>
              ) : (
                <Badge pill bg="secondary-200" className="text-black">
                  {getPendingStatusText(record)}
                </Badge>
              )}
            </div>
          );
        },
      },
      ...(extraColumns || []),
    ],
    [extraColumns, getPendingStatusText, tCommon, tPages],
  );

  const defaultVisibleColumns = useMemo(() => {
    const defaultColumnsConfiguration = Object.fromEntries(
      columns.map((column) => [
        getColumnKey(column),
        getColumnKey(column) !== DEFAULT_COLS_KEY.USERNAME,
      ]),
    );
    return AIIntegratedResultsColsKeys ?? defaultColumnsConfiguration;
  }, [AIIntegratedResultsColsKeys, columns]);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    defaultVisibleColumns,
  );

  const handleToggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const updatedVisibleColumns = {
        ...prev,
        [key]: !prev[key],
      };

      setAIIntegratedResultsColsKeys(updatedVisibleColumns);

      return updatedVisibleColumns;
    });
  };

  const displayedColumns = useMemo(
    () => columns.filter((column) => visibleColumns[getColumnKey(column)]),
    [columns, visibleColumns],
  );

  const history = useHistory();

  return (
    <Stack className="h-100" gap={3}>
      <Stack
        direction="horizontal"
        gap={4}
        className="flex-wrap align-items-end"
      >
        <Form
          onSubmit={handleSubmit(handleSearch)}
          ref={submitRef}
          className="flex-fill"
        >
          <Form.Group as={Col} controlId="searchValue">
            <InputGroup>
              <Form.Control
                autoComplete="off"
                type="text"
                className="rounded-3 me-2"
                placeholder={tPages('results.searchPlaceholder')}
                {...register('searchValue', {
                  maxLength: {
                    value: 100,
                    message: tPages('results.searchMaxLength'),
                  },
                  validate: {
                    isValid: (value) =>
                      validateSearchValue(value) ||
                      tPages('results.searchInvalid'),
                  },
                  onChange: (event) =>
                    setValue('searchValue', event.target.value, {
                      shouldValidate: true,
                    }),
                })}
              />

              <ButtonGroup size="sm">
                <Button variant="outline-primary" type="submit">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className={classNames({
                      'icon-shake': isValid && getValues('searchValue'),
                    })}
                  />
                </Button>
                <Button
                  variant="outline-warning"
                  onClick={handleReset}
                  disabled={!getValues('searchValue')}
                  data-test="reset-button"
                >
                  <FontAwesomeIcon icon={faClose} />
                </Button>
              </ButtonGroup>
            </InputGroup>
          </Form.Group>

          {errors.searchValue?.message && (
            <p className="text-danger">{errors.searchValue?.message}</p>
          )}
        </Form>

        <ColumnConfiguration
          columns={columns}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
        />
      </Stack>
      <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
        <div className="flex-grow-1" style={{ minHeight: 0 }}>
          <CustomTable
            isLoading={isLoading}
            dataSource={dataResource.sessions}
            rowKey="iid"
            columns={displayedColumns}
            pagination={{
              defaultPageSize,
              pageSizeOptions: [10],
              totalPages: dataResource.totalPages,
              currentPage,
              handleSetCurrentPage,
              onChangePage: (pageNumber, pageSize) => {
                handleLoadSessions(
                  pageNumber - 1,
                  pageSize,
                  currentSearchValue,
                  SortOrderEnum.DESC,
                );
              },
            }}
            empty={{
              buttonText: tPages('results.createNewExam'),
              onButtonClick: () => {
                history.push('/session-form');
              },
            }}
          />
        </div>
      </div>
    </Stack>
  );
};

export default OHResults;
